/* Addressing, as arithmetic.
 *
 * Two screens want the same answer from opposite directions. The Network screen
 * asks "what is on 10.10.10.0/24" — Hudu's IPAM, and the one thing it names as
 * something IT Glue cannot do. A device asks "which subnet am I on", which is
 * the relation that tells you where a machine actually sits without anybody
 * typing it twice.
 *
 * Both are the same containment test, so it lives here once, as functions with
 * no React and no service in them.
 *
 * IPv4 only, deliberately. Every subnet in the register is RFC1918 and a v6
 * implementation that is never exercised is a liability rather than a feature —
 * it would be wrong in some way nobody discovers for two years.
 */

import type { IDevice, ISubnet } from '../models';

/** Dotted quad to a number, or null when it is not an address.
 *
 *  Strict on purpose: '10.10.10' and '10.10.10.256' both come back null rather
 *  than being coerced into something plausible. A wrong answer here puts a
 *  machine on the wrong VLAN in a diagram somebody trusts. */
export function ipToLong(ip: string): number | null {
  const parts = ip.trim().split('.');
  if (parts.length !== 4) return null;

  let out = 0;
  for (const part of parts) {
    if (!/^\d{1,3}$/.test(part)) return null;
    const n = Number(part);
    if (n > 255) return null;
    out = out * 256 + n;
  }
  return out;
}

export function longToIp(value: number): string {
  return [24, 16, 8, 0].map((shift) => (value >>> shift) & 255).join('.');
}

export interface Cidr {
  /** First address in the block, including the network address itself. */
  base: number;
  /** Prefix length — the 24 in /24. */
  bits: number;
  /** Last address in the block, including the broadcast address. */
  last: number;
}

export function parseCidr(cidr: string): Cidr | null {
  const [address, prefix] = cidr.trim().split('/');
  if (!address || prefix === undefined) return null;

  const bits = Number(prefix);
  if (!/^\d{1,2}$/.test(prefix) || bits > 32) return null;

  const ip = ipToLong(address);
  if (ip === null) return null;

  /* A /0 shifts by 32, which in JavaScript shifts by 0 and returns -1 rather
     than 0. Handled rather than avoided: somebody will paste 0.0.0.0/0 into the
     box one day, and it should read as "everything", not "nothing". */
  const mask = bits === 0 ? 0 : (0xffffffff << (32 - bits)) >>> 0;
  const base = (ip & mask) >>> 0;
  const size = bits === 0 ? 0x100000000 : 2 ** (32 - bits);

  return { base, bits, last: base + size - 1 };
}

export function ipInCidr(ip: string, cidr: string): boolean {
  const value = ipToLong(ip);
  const block = parseCidr(cidr);
  if (value === null || !block) return false;
  return value >= block.base && value <= block.last;
}

/** Which subnet an address belongs to.
 *
 *  Most specific wins. A site with both 10.10.0.0/16 and 10.10.10.0/24
 *  recorded should say a machine is on the /24 — the broader block is the
 *  address plan, the narrower one is the VLAN somebody plugged it into. */
export function subnetFor(ip: string, subnets: readonly ISubnet[]): ISubnet | undefined {
  let best: ISubnet | undefined;
  let bestBits = -1;

  for (const subnet of subnets) {
    const block = parseCidr(subnet.cidr);
    if (!block || !ipInCidr(ip, subnet.cidr)) continue;
    if (block.bits > bestBits) {
      best = subnet;
      bestBits = block.bits;
    }
  }
  return best;
}

export interface Host {
  ip: string;
  device: IDevice;
  /** Inside the DHCP scope, where one is recorded. */
  dynamic: boolean;
  /** A lights-out controller rather than the machine itself.
   *
   *  A server occupies two addresses and they are usually on different VLANs —
   *  the OS on the corporate one, the iLO on management. Counting only the
   *  first made the management subnet read as empty, which is the one place
   *  somebody looks when they cannot reach anything else. */
  management: boolean;
}

export interface SubnetUsage {
  /** Addresses that can be assigned — the block less network and broadcast. */
  usable: number;
  /** Devices in the register carrying an address in this block. */
  hosts: Host[];
  /** Recorded statically, meaning outside the DHCP scope or where none is set. */
  staticCount: number;
  /** The DHCP scope, where it could be read. */
  scope: { from: number; to: number } | null;
}

/* Two addresses in every IPv4 block are not host addresses: the network address
   and the broadcast. A /31 and a /32 have neither in the usual sense, and
   reporting -1 usable addresses would be worse than reporting none. */
function usableCount(block: Cidr): number {
  const size = block.last - block.base + 1;
  return size > 2 ? size - 2 : 0;
}

/** A DHCP scope as somebody typed it.
 *
 *  Free text, because that is what people write: "10.10.10.100 – .200",
 *  "10.10.10.100-10.10.10.200", ".100 to .200". The last octet on its own is
 *  read against the subnet, so a shorthand nobody would expand by hand still
 *  produces a range. Unreadable is null, and a null scope simply means every
 *  host counts as static rather than the panel refusing to draw. */
export function parseScope(scope: string, block: Cidr): { from: number; to: number } | null {
  const parts = scope.split(/\s*(?:-|–|—|to)\s*/i).filter(Boolean);
  if (parts.length < 2) return null;

  const read = (text: string): number | null => {
    const full = ipToLong(text);
    if (full !== null) return full;
    // '.200' or '200' — an octet, relative to the block.
    const m = text.trim().match(/^\.?(\d{1,3})$/);
    if (!m || Number(m[1]) > 255) return null;
    return ((block.base >>> 8) << 8) + Number(m[1]);
  };

  const from = read(parts[0]);
  const to = read(parts[parts.length - 1]);
  if (from === null || to === null || to < from) return null;
  return { from, to };
}

/** What is actually on a subnet, from the devices that carry an address. */
export function subnetUsage(subnet: ISubnet, devices: readonly IDevice[]): SubnetUsage {
  const block = parseCidr(subnet.cidr);
  if (!block) return { usable: 0, hosts: [], staticCount: 0, scope: null };

  const scope = subnet.dhcp && subnet.dhcpRange ? parseScope(subnet.dhcpRange, block) : null;

  const hosts: Host[] = [];
  const consider = (device: IDevice, ip: string | undefined, management: boolean) => {
    const value = ipToLong(ip ?? '');
    if (value === null || value < block.base || value > block.last) return;
    hosts.push({
      ip: ip!,
      device,
      dynamic: scope !== null && value >= scope.from && value <= scope.to,
      management,
    });
  };

  for (const device of devices) {
    consider(device, device.ip, false);
    /* The second address. A controller is on the network whether or not the
       machine it is bolted to is, so it takes up an address either way. */
    consider(device, device.mgmtAddress, true);
  }

  hosts.sort((a, b) => (ipToLong(a.ip) ?? 0) - (ipToLong(b.ip) ?? 0));

  return {
    usable: usableCount(block),
    hosts,
    staticCount: hosts.filter((h) => !h.dynamic).length,
    scope,
  };
}

/** Addresses recorded twice.
 *
 *  The finding IPAM exists for. Two machines on one address is a fault that
 *  presents as "the network is flaky" and is invisible in a list sorted by
 *  anything other than address. */
export function duplicateIps(devices: readonly IDevice[]): { ip: string; devices: IDevice[] }[] {
  const byIp = new Map<string, IDevice[]>();
  for (const device of devices) {
    /* Both addresses. A lights-out controller sharing an address with a switch
       is exactly the clash nobody finds, because the two are documented in
       different places and neither list shows the other. */
    for (const ip of [device.ip, device.mgmtAddress]) {
      if (ipToLong(ip ?? '') === null) continue;
      const list = byIp.get(ip!) ?? [];
      list.push(device);
      byIp.set(ip!, list);
    }
  }
  return [...byIp.entries()]
    .filter(([, list]) => list.length > 1)
    .map(([ip, list]) => ({ ip, devices: list }))
    .sort((a, b) => (ipToLong(a.ip) ?? 0) - (ipToLong(b.ip) ?? 0));
}
